import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Mail, Plus, Send, Eye, Copy, Pause, Play, Ban, Trash2, CheckCircle, Clock, Calendar, MoreHorizontal, Edit, Users, Info, ChevronDown, ChevronUp, HelpCircle, FileText, AlertTriangle, Sparkles, ShieldCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Card, CardContent } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useCampaigns, useDeleteCampaign, useDuplicateCampaign, useUpdateCampaign, useProcessScheduledNow } from '@/hooks/useCampaigns';
import { useGenerateCampaignEmails, useVerifyCampaignEmails } from '@/hooks/useCampaignAI';
import { useCampaignTemplates } from '@/hooks/useCampaignTemplates';
import { Campaign } from '@/services/campaignService';
import CampaignCreateDialog from './CampaignCreateDialog';
import CampaignSendDialog from './CampaignSendDialog';
import CampaignPreviewDialog from './CampaignPreviewDialog';
import CampaignTestSendDialog from './CampaignTestSendDialog';
import CampaignRecipientsDialog from './CampaignRecipientsDialog';
import CampaignDetailDialog from './CampaignDetailDialog';
import CampaignEmailLogDialog from './CampaignEmailLogDialog';
import CampaignGenerateDialog from './CampaignGenerateDialog';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

interface Props {
  companyId: string;
}

const statusConfig: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ElementType; label: string }> = {
  draft: { variant: 'secondary', icon: Clock, label: 'Draft' },
  scheduled: { variant: 'outline', icon: Calendar, label: 'Scheduled' },
  sending: { variant: 'default', icon: Send, label: 'Sending' },
  sent: { variant: 'default', icon: CheckCircle, label: 'Sent' },
  paused: { variant: 'secondary', icon: Pause, label: 'Paused' },
  cancelled: { variant: 'destructive', icon: Ban, label: 'Cancelled' },
};

const CampaignManager: React.FC<Props> = ({ companyId }) => {
  const [verifyingCampaignId, setVerifyingCampaignId] = useState<string | null>(null);
  const [verifyProgress, setVerifyProgress] = useState<{ processed: number; total: number } | null>(null);
  const [reverifyCampaign, setReverifyCampaign] = useState<Campaign | null>(null);
  const { data: campaigns = [], isLoading } = useCampaigns(companyId);
  const deleteMutation = useDeleteCampaign();
  const duplicateMutation = useDuplicateCampaign();
  const updateMutation = useUpdateCampaign();
  const processNowMutation = useProcessScheduledNow();
  
  const generateAIMutation = useGenerateCampaignEmails();
  const verifyAIMutation = useVerifyCampaignEmails();
  const { data: templates = [] } = useCampaignTemplates(companyId);

  const runVerify = (campaignId: string, reverify: boolean) => {
    setVerifyingCampaignId(campaignId);
    setVerifyProgress(null);
    verifyAIMutation.mutate({
      campaignId,
      reverify,
      onProgress: (processed, total) => setVerifyProgress({ processed, total }),
    }, {
      onSettled: () => { setVerifyingCampaignId(null); setVerifyProgress(null); },
    });
  };


  const [createOpen, setCreateOpen] = useState(false);
  const [editCampaign, setEditCampaign] = useState<Campaign | null>(null);
  const [sendCampaign, setSendCampaign] = useState<Campaign | null>(null);
  const [previewCampaign, setPreviewCampaign] = useState<Campaign | null>(null);
  const [testSendCampaign, setTestSendCampaign] = useState<Campaign | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [recipientsCampaign, setRecipientsCampaign] = useState<Campaign | null>(null);
  const [detailCampaign, setDetailCampaign] = useState<Campaign | null>(null);
  const [emailLogCampaign, setEmailLogCampaign] = useState<Campaign | null>(null);
  const [generateCampaign, setGenerateCampaign] = useState<Campaign | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const queryClient = useQueryClient();

  // Email sending toggle
  const { data: emailSendingEnabled = true } = useQuery({
    queryKey: ['email-sending-enabled', companyId],
    queryFn: async () => {
      const { data } = await supabase
        .from('campaign_settings')
        .select('value')
        .eq('company_id', companyId)
        .eq('key', 'email_sending_enabled')
        .maybeSingle();
      if (!data) return false;
      return data.value === true;
    },
  });

  const toggleEmailSending = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data: existing } = await supabase
        .from('campaign_settings')
        .select('id')
        .eq('company_id', companyId)
        .eq('key', 'email_sending_enabled')
        .maybeSingle();

      if (existing) {
        await supabase
          .from('campaign_settings')
          .update({ value: enabled as any })
          .eq('id', existing.id);
      } else {
        await supabase
          .from('campaign_settings')
          .insert({ company_id: companyId, key: 'email_sending_enabled', value: enabled as any });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-sending-enabled', companyId] });
    },
  });

  const totalSent = campaigns.reduce((s, c) => s + (c.sent_count || 0), 0);
  const totalFailed = campaigns.reduce((s, c) => s + (c.failed_count || 0), 0);

  const getTargetLabel = (filters: any) => {
    if (!filters) return 'All Leads';
    if (filters.target === 'custom') return `Custom Recipients (${(filters.recipients || []).length})`;
    if (filters.target === 'by_status') return `Status: ${(filters.statuses || []).join(', ')}`;
    if (filters.target === 'by_category') return `Category: ${(filters.categories || []).join(', ')}`;
    if (filters.target === 'from_queue') return 'From Recipient List';
    return 'All Leads';
  };

  const hasTemplate = (campaign: Campaign) => !!campaign.template_id;

  return (
    <div className="space-y-4">
      {/* Email sending toggle */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Switch
            checked={emailSendingEnabled}
            onCheckedChange={(checked) => toggleEmailSending.mutate(checked)}
          />
          <Label className="text-sm font-medium">Email Sending</Label>
        </div>
      </div>

      {!emailSendingEnabled && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-orange-300 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800 text-sm">
          <AlertTriangle className="h-4 w-4 text-orange-600 shrink-0" />
          <span className="text-orange-800 dark:text-orange-300">
            Email sending is disabled. Campaigns will be processed in dry-run mode — no emails will actually be sent. Emails will be logged as "simulated". View simulated emails via <strong>"View Email Log"</strong> in each campaign's action menu (⋯).
          </span>
        </div>
      )}

      {/* Workflow help */}
      <Collapsible open={helpOpen} onOpenChange={setHelpOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-foreground">
            <HelpCircle className="h-4 w-4" />
            How campaigns work
            {helpOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 p-4 rounded-lg border bg-muted/30 space-y-2 text-sm">
            <p className="font-medium">Mail Merge → Campaign Workflow</p>
            <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
              <li><strong>Generate Mail Merge</strong> — Create personalized emails from your leads using AI</li>
              <li><strong>Convert to Campaign</strong> — Turn a mail merge into a draft campaign with all personalized content queued</li>
              <li><strong>Test Send</strong> — Send a test email to yourself to preview how it looks in an inbox</li>
              <li><strong>Send Campaign</strong> — Review recipients, choose your sender identity, and send to all matching leads</li>
            </ol>
            <p className="text-muted-foreground pt-1">
              Emails are queued and sent in batches. You can pause, resume, or cancel a campaign at any time from the actions menu.
            </p>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><div className="text-sm text-muted-foreground">Total Campaigns</div><div className="text-2xl font-bold">{campaigns.length}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-sm text-muted-foreground">Emails Sent</div><div className="text-2xl font-bold">{totalSent}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-sm text-muted-foreground">Failed</div><div className="text-2xl font-bold">{totalFailed}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-sm text-muted-foreground">Active</div><div className="text-2xl font-bold">{campaigns.filter(c => c.status === 'sending').length}</div></CardContent></Card>
      </div>

      {/* Actions */}
      <div className="flex justify-end">
        <Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4 mr-1.5" />Create Campaign</Button>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Campaign</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden md:table-cell">Target</TableHead>
              <TableHead className="hidden sm:table-cell">Recipients</TableHead>
              <TableHead className="hidden sm:table-cell">Sent</TableHead>
              <TableHead className="hidden sm:table-cell">Failed</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : campaigns.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No campaigns yet. Create your first one!</TableCell></TableRow>
            ) : campaigns.map(c => {
              const cfg = statusConfig[c.status] || statusConfig.draft;
              const StatusIcon = cfg.icon;
              return (
                <TableRow key={c.id}>
                  <TableCell>
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground truncate max-w-[200px]">{c.subject}</div>
                    {c.status === 'scheduled' && c.scheduled_for && (
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(c.scheduled_for), 'MMM d, yyyy h:mm a')}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={cfg.variant} className="gap-1">
                      <StatusIcon className="h-3 w-3" />
                      {cfg.label}
                    </Badge>
                    {verifyingCampaignId === c.id && (
                      <Badge variant="outline" className="gap-1 ml-2">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {verifyProgress ? `Verifying ${verifyProgress.processed}/${verifyProgress.total}` : 'Verifying...'}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm">{getTargetLabel(c.recipient_filters)}</TableCell>
                  <TableCell className="hidden sm:table-cell">{c.total_recipients}</TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <span className={c.sent_count > 0 ? 'text-green-600 font-medium' : ''}>{c.sent_count}</span>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <span className={c.failed_count > 0 ? 'text-destructive font-medium' : ''}>{c.failed_count}</span>
                  </TableCell>
                  <TableCell>
                    <TooltipProvider delayDuration={300}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {['draft', 'scheduled'].includes(c.status) && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <DropdownMenuItem onClick={() => setSendCampaign(c)}>
                                  <Send className="h-4 w-4 mr-2" />Send Campaign
                                </DropdownMenuItem>
                              </TooltipTrigger>
                              <TooltipContent side="left">Review recipients and sender, then send to all</TooltipContent>
                            </Tooltip>
                          )}
                          {['draft', 'scheduled'].includes(c.status) && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <DropdownMenuItem onClick={() => setTestSendCampaign(c)}>
                                  <Mail className="h-4 w-4 mr-2" />Test Send
                                </DropdownMenuItem>
                              </TooltipTrigger>
                              <TooltipContent side="left">Send a test email to yourself to preview how it looks</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <DropdownMenuItem onClick={() => setPreviewCampaign(c)}>
                                <Eye className="h-4 w-4 mr-2" />Preview
                              </DropdownMenuItem>
                            </TooltipTrigger>
                            <TooltipContent side="left">Preview email content without sending</TooltipContent>
                          </Tooltip>
                          <DropdownMenuItem onClick={() => setDetailCampaign(c)}><Info className="h-4 w-4 mr-2" />View Details</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setRecipientsCampaign(c)}><Users className="h-4 w-4 mr-2" />View Recipients</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setEmailLogCampaign(c)}><FileText className="h-4 w-4 mr-2" />View Email Log</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setEditCampaign(c)}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                          {/* Generate & Verify Actions */}
                          {['draft', 'scheduled'].includes(c.status) && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setGenerateCampaign(c)}>
                                <Sparkles className="h-4 w-4 mr-2" />
                                Generate Emails
                              </DropdownMenuItem>
                            </>
                          )}
                          {c.total_recipients > 0 && ['draft', 'scheduled'].includes(c.status) && (
                            <>
                              <DropdownMenuItem
                                onClick={() => runVerify(c.id, false)}
                                disabled={verifyingCampaignId === c.id}
                              >
                                {verifyingCampaignId === c.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                                Verify Emails (AI)
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setReverifyCampaign(c)}
                                disabled={verifyingCampaignId === c.id}
                              >
                                <ShieldCheck className="h-4 w-4 mr-2" />
                                Re-verify All Emails (AI)
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => duplicateMutation.mutate(c.id)}><Copy className="h-4 w-4 mr-2" />Duplicate</DropdownMenuItem>
                          {['sent', 'cancelled', 'paused'].includes(c.status) && (
                            <DropdownMenuItem onClick={async () => {
                              await supabase.from('email_queue')
                                .update({ status: 'pending', sent_at: null, error_message: null, attempts: 0, failed_at: null })
                                .eq('campaign_id', c.id)
                                .in('status', ['sent', 'failed']);
                              updateMutation.mutate({ id: c.id, updates: { status: 'draft', sent_count: 0, failed_count: 0, started_at: null } });
                            }}>
                              <Edit className="h-4 w-4 mr-2" />Reset to Draft
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {c.status === 'sending' && (
                            <DropdownMenuItem onClick={() => updateMutation.mutate({ id: c.id, updates: { status: 'paused' } })}><Pause className="h-4 w-4 mr-2" />Pause</DropdownMenuItem>
                          )}
                          {c.status === 'scheduled' && (
                            <DropdownMenuItem onClick={() => processNowMutation.mutate(c.id)}><Play className="h-4 w-4 mr-2" />Send Now</DropdownMenuItem>
                          )}
                          {c.status === 'paused' && (
                            <DropdownMenuItem onClick={() => updateMutation.mutate({ id: c.id, updates: { status: 'sending' } })}><Play className="h-4 w-4 mr-2" />Resume</DropdownMenuItem>
                          )}
                          {['sending', 'paused', 'scheduled'].includes(c.status) && (
                            <DropdownMenuItem onClick={() => updateMutation.mutate({ id: c.id, updates: { status: 'cancelled', cancelled_at: new Date().toISOString() } })}><Ban className="h-4 w-4 mr-2" />Cancel</DropdownMenuItem>
                          )}
                          {['draft', 'cancelled', 'sent'].includes(c.status) && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive" onClick={() => setDeleteId(c.id)}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TooltipProvider>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Dialogs */}
      <CampaignCreateDialog open={createOpen} onOpenChange={setCreateOpen} companyId={companyId} />
      {editCampaign && <CampaignCreateDialog open={!!editCampaign} onOpenChange={() => setEditCampaign(null)} companyId={companyId} campaign={editCampaign} />}
      {sendCampaign && <CampaignSendDialog open={!!sendCampaign} onOpenChange={() => setSendCampaign(null)} campaign={sendCampaign} />}
      {previewCampaign && <CampaignPreviewDialog open={!!previewCampaign} onOpenChange={() => setPreviewCampaign(null)} campaign={previewCampaign} />}
      {testSendCampaign && <CampaignTestSendDialog open={!!testSendCampaign} onOpenChange={() => setTestSendCampaign(null)} campaign={testSendCampaign} />}
      {recipientsCampaign && <CampaignRecipientsDialog open={!!recipientsCampaign} onOpenChange={() => setRecipientsCampaign(null)} campaign={recipientsCampaign} />}
      {detailCampaign && <CampaignDetailDialog open={!!detailCampaign} onOpenChange={() => setDetailCampaign(null)} campaign={detailCampaign} />}
      {emailLogCampaign && <CampaignEmailLogDialog open={!!emailLogCampaign} onOpenChange={() => setEmailLogCampaign(null)} campaign={emailLogCampaign} />}
      {generateCampaign && <CampaignGenerateDialog open={!!generateCampaign} onOpenChange={() => setGenerateCampaign(null)} campaign={generateCampaign} />}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Campaign?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteId) { deleteMutation.mutate(deleteId); setDeleteId(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!reverifyCampaign} onOpenChange={(open) => !open && setReverifyCampaign(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-verify all emails?</AlertDialogTitle>
            <AlertDialogDescription>
              This will clear existing verification results for{' '}
              <strong>{reverifyCampaign?.total_recipients ?? 0}</strong> recipient
              {(reverifyCampaign?.total_recipients ?? 0) === 1 ? '' : 's'} in
              "{reverifyCampaign?.name}" and run AI verification again. This may take a
              few minutes and will consume Gemini quota.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (reverifyCampaign) {
                  runVerify(reverifyCampaign.id, true);
                  setReverifyCampaign(null);
                }
              }}
            >
              Re-verify all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CampaignManager;
